import { Aide } from '@/components/ui/aide';
import React from 'react';

import { ChampsAvances } from '../ui/champs-avances';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { libelle } from '../../lib/libelles';
import {
  CATEGORIES_MATRICE,
  FOURNISSEURS_CHAUFFEUR,
  MODES_ENTRANTS,
  MODES_REGLEMENT,
  MODE_INDICES,
  PORTEURS,
  UNITES_PERIODE,
  etiquetteTarif,
} from '../../lib/inboundRentalsForm';
import { cn } from '../../lib/utils';

/**
 * Les conditions d'un contrat de location reçue: mode de rémunération, tarifs,
 * dates, chauffeur, « qui paie quoi ». Servent à la création (fenêtre de
 * l'onglet des locations) et à la modification (onglet « Conditions »).
 *
 * Les tarifs ne s'affichent qu'à la direction (`canSeeMoney`): la logistique
 * prépare le contrat, la direction saisit les prix.
 */

const ChampNombre = ({ id, label, value, onChange, placeholder, suffixe, step }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min="0"
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn('text-right tabular-nums', suffixe && 'pr-14')}
      />
      {suffixe ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {suffixe}
        </span>
      ) : null}
    </div>
  </div>
);

const InboundConditionsFields = ({
  form,
  setField,
  setPayer,
  canSeeMoney,
  idPrefix = 'cond',
  companyOptions,
  onCompanyChange,
  errors = {},
  submitAttempted = false,
  disabled = false,
  numerotation = null,
}) => {
  const mode = form.mode;
  const montrerTarifs = canSeeMoney;

  return (
    <div className="space-y-5">
      {companyOptions ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-transporteur`}>Transporteur (propriétaire des camions)</Label>
          <SearchableSelect
            id={`${idPrefix}-transporteur`}
            placeholder="Choisir le transporteur"
            searchPlaceholder="Rechercher un transporteur…"
            emptyMessage="Aucune société trouvée"
            value={form.company_id || ''}
            onValueChange={onCompanyChange}
            options={companyOptions}
            disabled={disabled}
          />
          <FieldError show={submitAttempted && errors.company_id}>
            Choisissez le transporteur qui vous loue les camions.
          </FieldError>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label>Comment le propriétaire est rémunéré</Label>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" role="group" aria-label="Mode de rémunération">
          {MODES_ENTRANTS.map((code) => (
            <button
              key={code}
              type="button"
              disabled={disabled}
              onClick={() => setField('mode', code)}
              aria-pressed={mode === code}
              data-testid={`mode-${code}`}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors disabled:opacity-60',
                mode === code ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
              )}
            >
              <span className="block text-sm font-medium">{libelle('mode_remuneration_location', code)}</span>
              <span className="block text-xs text-muted-foreground">{MODE_INDICES[code]}</span>
            </button>
          ))}
        </div>
      </div>

      {montrerTarifs ? (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <p className="text-sm font-medium">Tarif</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {mode === 'PERIODE' ? (
              <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}-unite`}>Période du fret</Label>
                <Select
                  value={form.period_unit}
                  onValueChange={(valeur) => setField('period_unit', valeur)}
                  disabled={disabled}
                >
                  <SelectTrigger id={`${idPrefix}-unite`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITES_PERIODE.map((code) => (
                      <SelectItem key={code} value={code}>
                        {libelle('unite_periode', code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {['PERIODE', 'ROTATION', 'LITRE'].includes(mode) ? (
              <ChampNombre
                id={`${idPrefix}-tarif`}
                label={etiquetteTarif(mode, form.period_unit)}
                value={form.rate_amount}
                onChange={(valeur) => setField('rate_amount', valeur)}
                suffixe="FCFA"
                step={mode === 'LITRE' ? '0.01' : '1'}
              />
            ) : null}

            {mode === 'PART_FRET' ? (
              <div>
                <ChampNombre
                  id={`${idPrefix}-part`}
                  label="Part du fret versée au propriétaire"
                  value={form.share_percent}
                  onChange={(valeur) => setField('share_percent', valeur)}
                  suffixe="%"
                  step="0.1"
                />
                <FieldError show={submitAttempted && errors.share_percent}>
                  Indiquez un pourcentage entre 0 et 100.
                </FieldError>
              </div>
            ) : null}

            {mode === 'GERE' ? (
              <div>
                <ChampNombre
                  id={`${idPrefix}-commission`}
                  label="Commission de la société"
                  value={form.commission_percent}
                  onChange={(valeur) => setField('commission_percent', valeur)}
                  suffixe="%"
                  step="0.1"
                />
                <FieldError show={submitAttempted && errors.commission_percent}>
                  Indiquez un pourcentage entre 0 et 100 (0 si aucune commission).
                </FieldError>
              </div>
            ) : null}
          </div>
          {mode === 'GERE' ? (
            <Aide titre="Contrat géré" libelle="Comment le propriétaire est payé">
              Le propriétaire reçoit le fret des rotations, moins les frais mis à sa charge ci-dessous,
              moins votre commission.
            </Aide>
          ) : null}
          {['PERIODE', 'ROTATION', 'LITRE'].includes(mode) ? (
            <Aide titre="Tarif par camion" libelle="Tarif par camion">
              Le tarif peut aussi être fixé camion par camion, dans l’onglet « Camions du contrat ».
            </Aide>
          ) : null}
        </div>
      ) : (
        <Aide titre="Tarifs" libelle="Qui saisit les tarifs ?">
          Les tarifs sont saisis et consultés par la direction. Vous préparez le contrat, elle y ajoutera les prix.
        </Aide>
      )}

      {montrerTarifs ? (
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3" data-testid="mode-reglement-bloc">
          <div className="space-y-1.5">
            <Label id={`${idPrefix}-reglement-libelle`}>Mode de règlement</Label>
            <div
              className="inline-flex gap-0.5 rounded-lg border border-border/70 bg-card p-0.5"
              role="group"
              aria-labelledby={`${idPrefix}-reglement-libelle`}
            >
              {MODES_REGLEMENT.map((code) => {
                const actif = (form.settlement_method || 'MENSUEL') === code;
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={disabled}
                    aria-pressed={actif}
                    data-testid={`reglement-${code}`}
                    onClick={() => setField('settlement_method', code)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60',
                      actif
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    {libelle('mode_reglement_location', code)}
                  </button>
                );
              })}
            </div>
          </div>
          {form.settlement_method === 'ETAT' ? (
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-sigle`}>Sigle du transporteur</Label>
              <Input
                id={`${idPrefix}-sigle`}
                value={form.statement_code || ''}
                maxLength={20}
                placeholder="BPS"
                disabled={disabled}
                onChange={(event) => setField('statement_code', event.target.value.toUpperCase())}
                className="w-32 uppercase"
                data-testid="statement-code"
              />
            </div>
          ) : null}
          {form.settlement_method === 'ETAT' ? numerotation : null}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-debut`}>Début du contrat</Label>
          <Input
            id={`${idPrefix}-debut`}
            type="date"
            value={form.start_date}
            disabled={disabled}
            onChange={(event) => setField('start_date', event.target.value)}
          />
          <FieldError show={submitAttempted && errors.start_date}>La date de début est obligatoire.</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-fin`}>Fin prévue</Label>
          <Input
            id={`${idPrefix}-fin`}
            type="date"
            value={form.end_date}
            disabled={disabled}
            onChange={(event) => setField('end_date', event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-chauffeur`}>Chauffeur</Label>
          <Select
            value={form.driver_provider}
            onValueChange={(valeur) => setField('driver_provider', valeur)}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-chauffeur`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FOURNISSEURS_CHAUFFEUR.map((code) => (
                <SelectItem key={code} value={code}>
                  {libelle('fournisseur_chauffeur', code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium">Qui paie quoi</p>
            <Aide titre="Qui paie quoi">Ce que le propriétaire supporte est déduit de son règlement du mois.</Aide>
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="matrice-qui-paie">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-semibold">Frais</th>
                <th className="px-3 py-2 font-semibold">Payé par</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES_MATRICE.map((categorie) => (
                <tr key={categorie} className="border-t border-border/70">
                  <td className="px-3 py-1.5">{libelle('categorie_frais', categorie)}</td>
                  <td className="px-3 py-1.5">
                    <div className="inline-flex gap-0.5 rounded-lg border border-border/70 bg-card p-0.5" role="group" aria-label={libelle('categorie_frais', categorie)}>
                      {PORTEURS.map((porteur) => {
                        const actif = (form.payer_matrix?.[categorie] || 'SOCIETE') === porteur;
                        return (
                          <button
                            key={porteur}
                            type="button"
                            disabled={disabled}
                            aria-pressed={actif}
                            data-testid={`qui-paie-${categorie}-${porteur}`}
                            onClick={() => setPayer(categorie, porteur)}
                            className={cn(
                              'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                              actif
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                          >
                            {libelle('porteur_frais', porteur)}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ChampsAvances
        titre="Plus de détails"
        consequence="Numéro du contrat, alerte de fin, dépassements et notes."
        nombre={
          2 + (montrerTarifs && ['PERIODE', 'ROTATION', 'LITRE'].includes(mode) ? 3 : 0) + (companyOptions ? 1 : 0)
        }
        valeurs={[
          form.contract_number,
          form.notes,
          form.included_units,
          form.overage_rate_amount,
          form.minimum_billable_units,
        ]}
      >
        {companyOptions ? (
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-numero`}>Numéro du contrat</Label>
            <Input
              id={`${idPrefix}-numero`}
              value={form.contract_number}
              placeholder="Attribué automatiquement si vous le laissez vide"
              onChange={(event) => setField('contract_number', event.target.value)}
            />
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <ChampNombre
            id={`${idPrefix}-alerte`}
            label="Prévenir avant la fin du contrat"
            value={form.alert_days_before}
            onChange={(valeur) => setField('alert_days_before', valeur)}
            suffixe="jours"
          />
        </div>
        {montrerTarifs && ['PERIODE', 'ROTATION', 'LITRE'].includes(mode) ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <ChampNombre
              id={`${idPrefix}-comprises`}
              label="Unités comprises"
              value={form.included_units}
              onChange={(valeur) => setField('included_units', valeur)}
              placeholder="Facultatif"
            />
            <ChampNombre
              id={`${idPrefix}-depassement`}
              label="Tarif de dépassement"
              value={form.overage_rate_amount}
              onChange={(valeur) => setField('overage_rate_amount', valeur)}
              placeholder="Facultatif"
              suffixe="FCFA"
            />
            <ChampNombre
              id={`${idPrefix}-plancher`}
              label="Minimum facturé"
              value={form.minimum_billable_units}
              onChange={(valeur) => setField('minimum_billable_units', valeur)}
              placeholder="Facultatif"
            />
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-notes`}>Notes</Label>
          <Textarea
            id={`${idPrefix}-notes`}
            rows={2}
            value={form.notes}
            placeholder="Facultatif"
            onChange={(event) => setField('notes', event.target.value)}
          />
        </div>
      </ChampsAvances>
    </div>
  );
};

export default InboundConditionsFields;
